import {
  PartConfirmationBucketEntry,
  PartConfirmationRequestPart,
  PartQuantity,
  SelectedSupplier,
  SupplierPart,
} from "./partsCalculationUtils";

export type PartConfirmationPartStatus =
  | "pending"
  | "confirmed"
  | "declined"
  | string;

export type PartConfirmationResponsePart = {
  partId?: string;
  partNumber?: string;
  rootPartNumber?: string;
  partName?: string;
  askedQty?: number;
  quantity?: number;
  status?: PartConfirmationPartStatus;
  responseStatus?: PartConfirmationPartStatus;
  confirmedQty?: number | null;
};

export type PartConfirmationOrderSupplier = {
  supplierId: string;
  supplierName?: string;
  parts: PartConfirmationResponsePart[];
};

export const resolveResponseStatus = (
  part: PartConfirmationResponsePart,
): "pending" | "confirmed" | "declined" => {
  const status = String(part.responseStatus ?? part.status ?? "")
    .trim()
    .toLowerCase();
  if (status === "declined") return "declined";
  if (status === "confirmed") return "confirmed";
  return "pending";
};

const resolveConfirmedQuantity = (
  part: PartConfirmationResponsePart,
): number => {
  if (part.confirmedQty !== undefined && part.confirmedQty !== null) {
    return Number(part.confirmedQty) || 0;
  }
  return Number(part.quantity) || 0;
};

/**
 * Generate Estimate is allowed only when:
 * - nothing is staged in the Part Confirmation bucket, and
 * - every part on every PC request for this enquiry is confirmed or declined
 *   (or there are no PC requests).
 */
export const canGenerateEstimateWithPartConfirmation = (
  responsesBySupplierId: Map<string, PartConfirmationResponsePart[]>,
  stagedPartConfirmationCount = 0,
): { allowed: boolean; reason: string } => {
  if (stagedPartConfirmationCount > 0) {
    return {
      allowed: false,
      reason: "Send Part Confirmation before generating estimate",
    };
  }
  if (responsesBySupplierId.size === 0) {
    return { allowed: true, reason: "" };
  }
  for (const parts of Array.from(responsesBySupplierId.values())) {
    const hasPending = (parts ?? []).some(
      (part: PartConfirmationResponsePart) =>
        resolveResponseStatus(part) === "pending",
    );
    if (hasPending) {
      return {
        allowed: false,
        reason: "Waiting for part confirmation responses",
      };
    }
  }
  return { allowed: true, reason: "" };
};

/**
 * Asked qty already sent per partId for a supplier (sum across responses).
 * Used so re-add only stages the positive delta.
 */
export const buildAlreadyAskedQtyByPartId = (
  parts: PartConfirmationResponsePart[] | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const part of parts ?? []) {
    const partId = String(part.partId ?? "").trim();
    if (!partId) continue;
    const asked = Math.max(0, Number(part.askedQty) || 0);
    map.set(partId, (map.get(partId) ?? 0) + asked);
  }
  return map;
};

const mergePartsByRoot = (parts: SupplierPart[]): SupplierPart[] => {
  const byRoot = new Map<string, number>();
  for (const part of parts) {
    const root = String(part.rootPartNumber ?? "").trim();
    if (!root) continue;
    byRoot.set(
      root,
      (byRoot.get(root) ?? 0) + Math.max(0, Number(part.familyQuantity) || 0),
    );
  }
  return Array.from(byRoot.entries())
    .filter(([, qty]) => qty > 0)
    .map(([rootPartNumber, familyQuantity]) => ({
      rootPartNumber,
      familyQuantity,
    }));
};

const buildEntryFromConfirmationParts = (
  base: Pick<PartConfirmationBucketEntry, "supplierId" | "supplierName" | "addedAt">,
  confirmationParts: PartConfirmationRequestPart[],
): PartConfirmationBucketEntry => {
  const parts = confirmationParts.filter(
    (part) => (Number(part.askedQty) || 0) > 0,
  );
  return {
    ...base,
    confirmationParts: parts,
    selectedParts: mergePartsByRoot(
      parts.map((part) => ({
        rootPartNumber: part.rootPartNumber,
        familyQuantity: part.askedQty,
      })),
    ),
  };
};

/**
 * Merge an incoming PC bucket increment into an existing entry (sum askedQty
 * per partId). Used so re-add updates totals (5 + 1 → 6) instead of replacing.
 */
export const mergePartConfirmationBucketEntries = (
  existing: PartConfirmationBucketEntry | undefined,
  incoming: PartConfirmationBucketEntry,
): PartConfirmationBucketEntry => {
  if (!existing) {
    return incoming;
  }

  const byPartId = new Map<string, PartConfirmationRequestPart>();
  for (const part of existing.confirmationParts) {
    const partId = String(part.partId ?? "").trim();
    if (!partId) continue;
    byPartId.set(partId, { ...part });
  }
  for (const part of incoming.confirmationParts) {
    const partId = String(part.partId ?? "").trim();
    if (!partId) continue;
    const prev = byPartId.get(partId);
    if (!prev) {
      byPartId.set(partId, { ...part });
      continue;
    }
    const askedQty =
      (Number(prev.askedQty) || 0) + (Number(part.askedQty) || 0);
    byPartId.set(partId, {
      ...prev,
      ...part,
      askedQty,
      // Prefer latest effective stock from the new increment; never force stock ≥ asked.
      stockQty: Math.max(
        Number(part.stockQty) || 0,
        Number(prev.stockQty) || 0,
      ),
    });
  }

  return buildEntryFromConfirmationParts(
    {
      supplierId: existing.supplierId,
      supplierName: incoming.supplierName || existing.supplierName,
      addedAt: existing.addedAt,
    },
    Array.from(byPartId.values()),
  );
};

/**
 * Reconcile API-hydrated pending with local staged totals using max askedQty
 * per partId. Preserves local 5+1=6 when API still only has the sent 5.
 */
export const reconcilePartConfirmationBucketEntry = (
  apiEntry: PartConfirmationBucketEntry | undefined,
  localEntry: PartConfirmationBucketEntry | undefined,
): PartConfirmationBucketEntry | null => {
  if (!apiEntry && !localEntry) return null;
  if (!apiEntry) return localEntry ?? null;
  if (!localEntry) return apiEntry;

  const byPartId = new Map<string, PartConfirmationRequestPart>();
  for (const part of [...apiEntry.confirmationParts, ...localEntry.confirmationParts]) {
    const partId = String(part.partId ?? "").trim();
    if (!partId) continue;
    const prev = byPartId.get(partId);
    if (!prev) {
      byPartId.set(partId, { ...part });
      continue;
    }
    const askedQty = Math.max(
      Number(prev.askedQty) || 0,
      Number(part.askedQty) || 0,
    );
    byPartId.set(partId, {
      ...prev,
      ...part,
      askedQty,
      stockQty: Math.max(
        Number(prev.stockQty) || 0,
        Number(part.stockQty) || 0,
      ),
    });
  }

  return buildEntryFromConfirmationParts(
    {
      supplierId: localEntry.supplierId || apiEntry.supplierId,
      supplierName: localEntry.supplierName || apiEntry.supplierName,
      addedAt: localEntry.addedAt || apiEntry.addedAt,
    },
    Array.from(byPartId.values()),
  );
};

/**
 * Build a display/base entry from API pending parts so re-add can merge onto
 * already-sent totals even if the local bucket was cleared.
 */
export const buildPartConfirmationEntryFromPendingResponses = (
  supplierId: string,
  supplierName: string,
  parts: PartConfirmationResponsePart[] | undefined,
): PartConfirmationBucketEntry | null => {
  const confirmationParts: PartConfirmationRequestPart[] = [];
  for (const part of parts ?? []) {
    if (resolveResponseStatus(part) !== "pending") continue;
    const partId = String(part.partId ?? "").trim();
    const rootPartNumber = String(part.rootPartNumber ?? "").trim();
    const askedQty = Math.max(0, Number(part.askedQty) || 0);
    if (!partId || !rootPartNumber || askedQty <= 0) continue;
    confirmationParts.push({
      partId,
      partNumber: part.partNumber || rootPartNumber,
      rootPartNumber,
      partName: part.partName,
      askedQty,
      stockQty: askedQty,
    });
  }
  if (confirmationParts.length === 0) {
    return null;
  }
  return {
    supplierId,
    supplierName,
    addedAt: new Date(),
    confirmationParts,
    selectedParts: mergePartsByRoot(
      confirmationParts.map((part) => ({
        rootPartNumber: part.rootPartNumber,
        familyQuantity: part.askedQty,
      })),
    ),
  };
};

/**
 * Reduce a staged PC entry to only positive delta vs already-sent asked qty.
 * Bucket entries must store TOTAL asked (including already sent); send uses this.
 */
export const applyAlreadySentDeltaToEntry = (
  entry: PartConfirmationBucketEntry,
  alreadyAskedByPartId: Map<string, number>,
): PartConfirmationBucketEntry | null => {
  if (alreadyAskedByPartId.size === 0) {
    return entry.confirmationParts.length > 0 ? entry : null;
  }

  const confirmationParts: PartConfirmationRequestPart[] = [];
  for (const part of entry.confirmationParts) {
    const already = alreadyAskedByPartId.get(String(part.partId)) ?? 0;
    const delta = Math.max(0, (Number(part.askedQty) || 0) - already);
    if (delta <= 0) continue;
    confirmationParts.push({
      ...part,
      askedQty: delta,
      // Keep effective stock at stamp time — never cap stockQty to the delta asked.
      stockQty: Math.max(0, Number(part.stockQty) || 0) || delta,
    });
  }
  if (confirmationParts.length === 0) {
    return null;
  }

  const selectedParts: SupplierPart[] = confirmationParts.map((part) => ({
    rootPartNumber: part.rootPartNumber,
    familyQuantity: part.askedQty,
  }));

  return {
    ...entry,
    confirmationParts,
    selectedParts,
  };
};

/**
 * Entries that still have unsent qty (new supplier / new parts / extra qty).
 * Empty → Send Part Confirmation should be disabled.
 */
export const getSendablePartConfirmationEntries = (
  bucket: PartConfirmationBucketEntry[],
  responsesBySupplierId: Map<string, PartConfirmationResponsePart[]>,
): PartConfirmationBucketEntry[] => {
  return bucket.flatMap((entry) => {
    const already = buildAlreadyAskedQtyByPartId(
      responsesBySupplierId.get(entry.supplierId),
    );
    const delta = applyAlreadySentDeltaToEntry(entry, already);
    return delta ? [delta] : [];
  });
};

/**
 * Qty that must leave Remaining Parts for PC:
 * - Bucket totals (includes staged + already-sent pending shown in PC bucket)
 * - API pending for suppliers not currently in the PC bucket
 *
 * Confirmed qty is owned by the Confirmed bucket (`selectedSuppliersInBucket`).
 * Do NOT also add bucket unsent-delta on top of API pending for the same supplier
 * when the bucket already holds cumulative totals.
 */
export const buildPartConfirmationCommittedSuppliers = (
  responsesBySupplierId: Map<string, PartConfirmationResponsePart[]>,
  partConfirmationBucket: PartConfirmationBucketEntry[],
): SelectedSupplier[] => {
  const committed: SelectedSupplier[] = [];
  const bucketSupplierIds = new Set(
    partConfirmationBucket.map((entry) => entry.supplierId),
  );

  // Prefer bucket totals when the supplier is staged/hydrated there.
  for (const entry of partConfirmationBucket) {
    const selectedParts = mergePartsByRoot(entry.selectedParts);
    if (selectedParts.length === 0) continue;
    committed.push({
      supplierId: entry.supplierId,
      supplierName: entry.supplierName,
      selectedParts,
      selectedAt: entry.addedAt,
    });
  }

  // API pending for suppliers removed from the PC bucket but still awaiting response.
  Array.from(responsesBySupplierId.entries()).forEach(([supplierId, parts]) => {
    if (bucketSupplierIds.has(supplierId)) return;
    const selectedParts: SupplierPart[] = [];
    for (const part of parts ?? []) {
      if (resolveResponseStatus(part) !== "pending") continue;
      const rootPartNumber = String(part.rootPartNumber ?? "").trim();
      const askedQty = Math.max(0, Number(part.askedQty) || 0);
      if (!rootPartNumber || askedQty <= 0) continue;
      selectedParts.push({ rootPartNumber, familyQuantity: askedQty });
    }
    const merged = mergePartsByRoot(selectedParts);
    if (merged.length === 0) return;
    committed.push({
      supplierId,
      supplierName: supplierId,
      selectedParts: merged,
      selectedAt: new Date(),
    });
  });

  return committed;
};

/**
 * Assigned qty for hydrate: pending → askedQty; confirmed → confirmed qty;
 * declined → 0 (goes to Remaining Parts).
 */
export const assignedQuantityForPartConfirmationPart = (
  part: PartConfirmationResponsePart,
): number => {
  const status = resolveResponseStatus(part);
  if (status === "declined") {
    return 0;
  }
  if (status === "confirmed") {
    return Math.max(0, resolveConfirmedQuantity(part));
  }
  return Math.max(0, Number(part.askedQty) || 0);
};

export type DualBucketHydration = {
  /** Pending / incomplete suppliers → Part Confirmation bucket. */
  partConfirmationEntries: PartConfirmationBucketEntry[];
  /** Confirmed qty only → Confirmed bucket (locked). */
  confirmedSuppliers: Array<Omit<SelectedSupplier, "selectedAt"> & { selectedAt?: Date }>;
};

/**
 * Split list-by-order PC responses into Part Confirmation (pending) and
 * Confirmed (locked, confirmed qty only). Fully responded suppliers leave PC.
 */
export const hydrateDualBucketsFromPartConfirmations = (
  suppliers: PartConfirmationOrderSupplier[],
  originalParts: PartQuantity[],
): DualBucketHydration => {
  const originalByRoot = new Map(
    originalParts.map((part) => [part.rootPartNumber, part]),
  );
  const originalByPartId = new Map(
    originalParts
      .filter((part) => part.partId)
      .map((part) => [String(part.partId), part]),
  );

  const partConfirmationEntries: PartConfirmationBucketEntry[] = [];
  const confirmedSuppliers: DualBucketHydration["confirmedSuppliers"] = [];

  for (const supplier of suppliers) {
    const pendingConfirmationParts: PartConfirmationRequestPart[] = [];
    const pendingSelectedParts: SupplierPart[] = [];
    const confirmedSelectedParts: SupplierPart[] = [];

    let hasPending = false;

    for (const part of supplier.parts ?? []) {
      const status = resolveResponseStatus(part);
      const rootPartNumber =
        part.rootPartNumber ||
        originalByPartId.get(String(part.partId ?? ""))?.rootPartNumber ||
        "";
      if (!rootPartNumber) continue;

      const original = originalByRoot.get(rootPartNumber);
      const askedQty = Math.max(0, Number(part.askedQty) || 0);

      if (status === "pending") {
        hasPending = true;
        if (askedQty > 0 && part.partId) {
          pendingConfirmationParts.push({
            partId: String(part.partId),
            partNumber:
              part.partNumber || original?.partNumber || rootPartNumber,
            rootPartNumber,
            partName: part.partName || original?.partName,
            askedQty,
            stockQty: askedQty,
          });
          pendingSelectedParts.push({
            rootPartNumber,
            familyQuantity: askedQty,
          });
        }
        continue;
      }

      if (status === "confirmed") {
        const confirmedQty = Math.max(0, resolveConfirmedQuantity(part));
        if (confirmedQty > 0) {
          confirmedSelectedParts.push({
            rootPartNumber,
            familyQuantity: confirmedQty,
          });
        }
      }
      // declined → Remaining Parts
    }

    if (hasPending && pendingConfirmationParts.length > 0) {
      partConfirmationEntries.push({
        supplierId: supplier.supplierId,
        supplierName: supplier.supplierName || supplier.supplierId,
        addedAt: new Date(),
        confirmationParts: pendingConfirmationParts,
        selectedParts: pendingSelectedParts,
      });
    }

    if (confirmedSelectedParts.length > 0) {
      confirmedSuppliers.push({
        supplierId: supplier.supplierId,
        supplierName: supplier.supplierName || supplier.supplierId,
        selectedParts: confirmedSelectedParts,
        selectedAt: new Date(),
        isPartConfirmationLocked: true,
      });
    }
  }

  return { partConfirmationEntries, confirmedSuppliers };
};
